import { useState, useEffect, useRef, useCallback } from "react";
import { API, setAuthToken } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Image, 
  Target, 
  Plus,
  MoreVertical,
  Edit,
  Copy,
  Trash2,
  ImageIcon,
  Download,
  Loader2,
  Check,
  Search,
  Code,
  Eye,
  MousePointerClick,
  Percent,
  ExternalLink,
  Info,
  Tags,
  Mail,
  MailX,
  SendHorizontal,
} from "lucide-react";
import MainLayout from "@/components/MainLayout";
import { useUser } from "@/contexts/UserContext";

// Banner Card Component
function BannerCard({ banner, bannerAnalytics, lotteries, onEdit, onClone, onDelete, onDownload, onCopyUrl, onCopySnippet, isDownloading, canEdit, canDelete, onToggleEmail, onSendTest }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Use the preview endpoint (no impression tracking) with lottery result timestamp as cache key
  const lottery = lotteries?.find(l => l.id === banner.lottery_id);
  const cacheKey = lottery?.result_updated_at || lottery?.created_at || '';
  const livePreviewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/banner-preview/${banner.id}?v=${encodeURIComponent(cacheKey)}`;
  
  // Get analytics for this banner
  const analytics = bannerAnalytics?.[banner.id] || { impressions: 0, clicks: 0, ctr: 0 };
  const isClickable = !!banner.destination_url;
  
  // Generate embed snippet
  const getEmbedSnippet = () => {
    const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/banner-image/${banner.id}`;
    const clickUrl = `${process.env.REACT_APP_BACKEND_URL}/api/click/${banner.id}`;
    
    if (banner.destination_url) {
      return `<a href="${clickUrl}" target="_blank" rel="noopener"><img src="${imageUrl}" alt="${banner.name || 'Banner'}" /></a>`;
    }
    return `<img src="${imageUrl}" alt="${banner.name || 'Banner'}" />`;
  };
  
  return (
    <Card
      className="bg-[#12121a] border-white/5 hover:border-[#10B981]/30 transition-all group overflow-hidden"
      data-testid={`banner-card-${banner.id}`}
    >
      <div className="relative">
        <div 
          className="relative bg-[#1a1a24] overflow-hidden"
          style={{ 
            paddingBottom: `${Math.min((banner.banner_height / banner.banner_width) * 100, 100)}%`,
            maxHeight: '180px'
          }}
        >
          {/* Show background image as fallback while live preview loads or on error */}
          {(!imageLoaded || imageError) && banner.background_image && (
            <img
              src={banner.background_image}
              alt={banner.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {/* Live rendered banner preview */}
          <img
            src={livePreviewUrl}
            alt={banner.name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity ${imageLoaded && !imageError ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            data-testid={`banner-preview-${banner.id}`}
          />
          <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
            {banner.banner_width} × {banner.banner_height}
          </div>
          {/* Clickable badge */}
          {isClickable && (
            <div className="absolute top-2 left-2 bg-[#10B981]/90 backdrop-blur-sm text-white text-xs px-2 py-1 rounded flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              Clickable
            </div>
          )}
        </div>
        
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="secondary" className="h-8 w-8 bg-black/70 hover:bg-black/90 border-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1a1a24] border-white/10">
              {canEdit && (
                <DropdownMenuItem 
                  onClick={onEdit}
                  className="text-gray-300 hover:text-white focus:text-white focus:bg-white/10"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {canEdit && (
                <DropdownMenuItem 
                  onClick={onClone}
                  className="text-gray-300 hover:text-white focus:text-white focus:bg-white/10"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Clone
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={() => onDownload("png")}
                disabled={isDownloading}
                className="text-gray-300 hover:text-white focus:text-white focus:bg-white/10"
              >
                {isDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download PNG
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDownload("jpeg")}
                disabled={isDownloading}
                className="text-gray-300 hover:text-white focus:text-white focus:bg-white/10"
              >
                {isDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download JPEG
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={() => onCopySnippet(getEmbedSnippet(), "Embed snippet copied!")}
                className="text-gray-300 hover:text-white focus:text-white focus:bg-white/10"
              >
                <Code className="w-4 h-4 mr-2" />
                Embed Snippet
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onCopyUrl(`${process.env.REACT_APP_BACKEND_URL}/api/banner-image/${banner.id}`, "Image URL copied!")}
                className="text-gray-300 hover:text-white focus:text-white focus:bg-white/10"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Copy Image URL
              </DropdownMenuItem>
              {canDelete && (
                <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={onDelete}
                    className="text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-white truncate text-sm flex-1 mr-2">{banner.name}</h3>
          <div className="flex items-center gap-2 shrink-0">
            {/* Email indicator — clickable to toggle */}
            <button
              onClick={(e) => { e.stopPropagation(); onToggleEmail && onToggleEmail(banner.id); }}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-colors ${
                banner.email_enabled
                  ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                  : "bg-gray-500/15 text-gray-500 hover:bg-gray-500/25"
              }`}
              title={banner.email_enabled ? "Email delivery ON — click to disable" : "Email delivery OFF — click to enable"}
              data-testid={`email-toggle-${banner.id}`}
            >
              {banner.email_enabled ? <Mail className="w-3 h-3" /> : <MailX className="w-3 h-3" />}
              <span>{(banner.recipients || []).length}</span>
            </button>
            {/* Send test email */}
            <button
              onClick={(e) => { e.stopPropagation(); onSendTest && onSendTest(banner.id); }}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 transition-colors"
              title="Send test email"
              data-testid={`send-test-${banner.id}`}
            >
              <SendHorizontal className="w-3 h-3" />
              <span>Test</span>
            </button>
            {/* Destination link — opens in new tab */}
            {isClickable && (
              <a
                href={banner.destination_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 transition-colors"
                title="Open destination in new tab"
                data-testid={`destination-link-${banner.id}`}
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-blue-400">
            <Eye className="w-3 h-3" />
            <span>{analytics.impressions.toLocaleString()} impressions</span>
          </div>
          {isClickable && (
            <div className="flex items-center gap-2">
              <span className="text-[#10B981]">{analytics.clicks} clicks</span>
              <span className="text-purple-400">{analytics.ctr.toFixed(1)}%</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { canEdit, canDelete, user } = useUser();
  const [banners, setBanners] = useState([]);
  const [lotteries, setLotteries] = useState([]);
  const [stats, setStats] = useState({ total_banners: 0, total_impressions: 0, total_clicks: 0 });
  const [bannerAnalytics, setBannerAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [downloadingBanner, setDownloadingBanner] = useState(null);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlToCopy, setUrlToCopy] = useState("");
  const [urlCopied, setUrlCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const urlInputRef = useRef(null);

  // Test email dialog state
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [testEmailBannerId, setTestEmailBannerId] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [bannersRes, lotteriesRes, analyticsRes, byBannerRes] = await Promise.all([
        axios.get(`${API}/banners`),
        axios.get(`${API}/lotteries`),
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/analytics/by-banner?page_size=1000`),
      ]);
      setBanners(bannersRes.data);
      setLotteries(lotteriesRes.data);
      setStats({
        total_banners: analyticsRes.data.total_banners || bannersRes.data.length,
        total_impressions: analyticsRes.data.total_impressions || 0,
        total_clicks: analyticsRes.data.total_clicks || 0,
      });
      
      const analyticsMap = {};
      (byBannerRes.data.data || []).forEach(item => {
        analyticsMap[item.banner_id] = {
          impressions: item.impressions || 0,
          clicks: item.clicks || 0,
          ctr: item.ctr || 0,
        };
      });
      setBannerAnalytics(analyticsMap);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = (bannerId) => {
    setBannerToDelete(bannerId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!bannerToDelete) return;
    try {
      await axios.delete(`${API}/banners/${bannerToDelete}`);
      toast.success("Banner deleted successfully");
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
      loadData();
    } catch (error) {
      toast.error("Failed to delete banner");
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
    }
  };

  const handleClone = async (bannerId) => {
    try {
      await axios.post(`${API}/banners/${bannerId}/clone`);
      toast.success("Banner cloned successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to clone banner");
    }
  };

  const handleToggleEmail = async (bannerId) => {
    try {
      const res = await axios.patch(`${API}/banners/${bannerId}/toggle-email`);
      const newVal = res.data.email_enabled;
      setBanners(prev => prev.map(b => b.id === bannerId ? { ...b, email_enabled: newVal } : b));
      toast.success(`Email delivery ${newVal ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error("Failed to toggle email");
    }
  };

  const openTestEmailDialog = (bannerId) => {
    setTestEmailBannerId(bannerId);
    setTestEmailAddress(user?.email || "");
    setTestEmailDialogOpen(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !testEmailBannerId) return;
    setSendingTestEmail(true);
    try {
      await axios.post(`${API}/banners/${testEmailBannerId}/send-test-email`, { email: testEmailAddress });
      toast.success(`Test email sent to ${testEmailAddress}`);
      setTestEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleDownload = async (bannerId, bannerName, format = "png") => {
    setDownloadingBanner(bannerId);
    
    try {
      const response = await fetch(`${API}/banners/${bannerId}/download?format=${format}`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = `${bannerName.replace(/\s+/g, '_')}.${format}`;
      
      // Create and click download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup blob URL after delay
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      
      toast.success(`Banner downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Download failed. Please try using 'Copy Image URL' and open in browser to save.");
    } finally {
      setDownloadingBanner(null);
    }
  };

  const copyToClipboard = async (text, message) => {
    // Method 1: Try using the modern Clipboard API
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(message);
        copied = true;
        return;
      }
    } catch (err) {
      console.error("Clipboard API unavailable:", err);
    }
    
    // If Clipboard API fails, show the dialog for manual copying
    if (!copied) {
      setUrlToCopy(text);
      setUrlCopied(false);
      setUrlDialogOpen(true);
    }
  };

  const handleCopyFromDialog = async () => {
    // Try clipboard API from dialog (user interaction might help)
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(urlToCopy);
        setUrlCopied(true);
        toast.success("URL copied!");
        setTimeout(() => setUrlDialogOpen(false), 1000);
        return;
      }
    } catch (err) {
      console.error("Clipboard write failed in dialog:", err);
    }

    // Fallback: select text for manual copy
    if (urlInputRef.current) {
      urlInputRef.current.select();
      urlInputRef.current.setSelectionRange(0, urlToCopy.length);
      try {
        const success = document.execCommand('copy');
        if (success) {
          setUrlCopied(true);
          toast.success("URL copied!");
          setTimeout(() => setUrlDialogOpen(false), 1000);
        } else {
          toast.info("Text selected - press Ctrl+C / Cmd+C to copy");
        }
      } catch (err) {
        toast.info("Text selected - press Ctrl+C / Cmd+C to copy");
      }
    }
  };

  // Filter banners by search query
  const filteredBanners = banners.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch brands for grouping
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    axios.get(`${API}/brands`).then(res => setBrands(res.data)).catch(() => {});
  }, []);

  // Group banners by lottery, then by brand within each lottery
  const bannersByLottery = lotteries.map(lottery => {
    const lotteryBanners = filteredBanners.filter(b => b.lottery_id === lottery.id);
    
    const brandGroups = brands.map(brand => ({
      brand,
      banners: lotteryBanners.filter(b => b.brand_id === brand.id)
    })).filter(g => g.banners.length > 0);
    
    const unbrandedBanners = lotteryBanners.filter(b => !b.brand_id || !brands.find(br => br.id === b.brand_id));
    
    return { lottery, brandGroups, unbrandedBanners, totalBanners: lotteryBanners.length };
  }).filter(group => group.totalBanners > 0);

  const orphanedBanners = filteredBanners.filter(b => !lotteries.find(l => l.id === b.lottery_id));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  const headerActions = (
    <>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          type="text"
          placeholder="Search banners..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 w-64 bg-[#1a1a24] border-white/10 text-white placeholder:text-gray-500 focus:border-[#10B981]"
          data-testid="search-banners-input"
        />
      </div>
      {canEdit && (
        <Button
          onClick={() => navigate("/banner-editor")}
          className="bg-[#10B981] hover:bg-[#059669] text-white"
          data-testid="create-banner-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Banner
        </Button>
      )}
    </>
  );

  return (
    <MainLayout 
      title="Dashboard" 
      subtitle="Manage your lottery banners"
      headerActions={headerActions}
    >
      <div>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Banners</p>
                  <p className="text-3xl font-bold text-white">{stats.total_banners}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                  <Image className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Impressions</p>
                  <p className="text-3xl font-bold text-white">{stats.total_impressions.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Clicks</p>
                  <p className="text-3xl font-bold text-white">{stats.total_clicks.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                  <MousePointerClick className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Lotteries</p>
                  <p className="text-3xl font-bold text-white">{lotteries.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Banners Grouped by Lottery */}
        {filteredBanners.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
              {searchQuery ? <Search className="w-10 h-10 text-gray-600" /> : <Image className="w-10 h-10 text-gray-600" />}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery ? "No banners found" : "No banners yet"}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery ? `No banners matching "${searchQuery}"` : "Create your first banner to get started"}
            </p>
            {!searchQuery && canEdit && (
              <Button
                onClick={() => navigate("/banner-editor")}
                className="bg-[#10B981] hover:bg-[#059669]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Banner
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {bannersByLottery.map(({ lottery, brandGroups, unbrandedBanners }) => (
              <div key={lottery.id}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                      <Target className="w-4 h-4 text-[#10B981]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{lottery.name}</h3>
                      <p className="text-xs text-gray-500">{(brandGroups.reduce((sum, g) => sum + g.banners.length, 0) + unbrandedBanners.length)} banner{(brandGroups.reduce((sum, g) => sum + g.banners.length, 0) + unbrandedBanners.length) !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/10 text-gray-400 hover:text-white"
                      onClick={() => navigate(`/banner-editor?lottery=${lottery.id}`)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Banner
                    </Button>
                  )}
                </div>
                
                {/* Brand sub-groups */}
                <div className="space-y-6 pl-4 border-l border-white/5">
                  {brandGroups.map(({ brand, banners: brandBanners }) => (
                    <div key={brand.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <Tags className="w-3.5 h-3.5 text-purple-400" />
                        <h4 className="text-sm font-medium text-purple-300">{brand.name}</h4>
                        <span className="text-xs text-gray-600">{brandBanners.length}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {brandBanners.map((banner) => (
                          <BannerCard 
                            key={banner.id} 
                            banner={banner}
                            bannerAnalytics={bannerAnalytics}
                            lotteries={lotteries}
                            onEdit={() => navigate(`/banner-editor/${banner.id}`)}
                            onClone={() => handleClone(banner.id)}
                            onDelete={() => handleDelete(banner.id)}
                            onDownload={(format) => handleDownload(banner.id, banner.name, format)}
                            onCopyUrl={(url, msg) => copyToClipboard(url, msg)}
                            onCopySnippet={(snippet, msg) => copyToClipboard(snippet, msg)}
                            isDownloading={downloadingBanner === banner.id}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onToggleEmail={handleToggleEmail}
                            onSendTest={openTestEmailDialog}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {/* Unbranded banners within this lottery */}
                  {unbrandedBanners.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Tags className="w-3.5 h-3.5 text-gray-500" />
                        <h4 className="text-sm font-medium text-gray-400">No Brand</h4>
                        <span className="text-xs text-gray-600">{unbrandedBanners.length}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {unbrandedBanners.map((banner) => (
                          <BannerCard 
                            key={banner.id} 
                            banner={banner}
                            bannerAnalytics={bannerAnalytics}
                            lotteries={lotteries}
                            onEdit={() => navigate(`/banner-editor/${banner.id}`)}
                            onClone={() => handleClone(banner.id)}
                            onDelete={() => handleDelete(banner.id)}
                            onDownload={(format) => handleDownload(banner.id, banner.name, format)}
                            onCopyUrl={(url, msg) => copyToClipboard(url, msg)}
                            onCopySnippet={(snippet, msg) => copyToClipboard(snippet, msg)}
                            isDownloading={downloadingBanner === banner.id}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onToggleEmail={handleToggleEmail}
                            onSendTest={openTestEmailDialog}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {orphanedBanners.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gray-500/20 flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Unassigned Banners</h3>
                    <p className="text-xs text-gray-500">{orphanedBanners.length} banner{orphanedBanners.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {orphanedBanners.map((banner) => (
                    <BannerCard 
                      key={banner.id} 
                      banner={banner}
                      bannerAnalytics={bannerAnalytics}
                      lotteries={lotteries}
                      onEdit={() => navigate(`/banner-editor/${banner.id}`)}
                      onClone={() => handleClone(banner.id)}
                      onDelete={() => handleDelete(banner.id)}
                      onDownload={(format) => handleDownload(banner.id, banner.name, format)}
                      onCopyUrl={(url, msg) => copyToClipboard(url, msg)}
                      onCopySnippet={(snippet, msg) => copyToClipboard(snippet, msg)}
                      isDownloading={downloadingBanner === banner.id}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onToggleEmail={handleToggleEmail}
                      onSendTest={openTestEmailDialog}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#12121a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Banner</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete this banner? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* URL Copy Dialog */}
      <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Copy URL</DialogTitle>
            <DialogDescription className="text-gray-400">
              Select the URL below and copy it (Ctrl+C / Cmd+C)
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              ref={urlInputRef}
              value={urlToCopy}
              readOnly
              className="flex-1 bg-[#1a1a24] border-white/10 text-white font-mono text-sm"
              onClick={(e) => e.target.select()}
              data-testid="url-copy-input"
            />
            <Button
              onClick={handleCopyFromDialog}
              className={`${urlCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-[#10B981] hover:bg-[#059669]'}`}
              data-testid="url-copy-button"
            >
              {urlCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Send Test Email</DialogTitle>
            <DialogDescription className="text-gray-400">
              Send a test copy of this banner email to the address below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 block mb-1.5">Email address</label>
              <Input
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="you@example.com"
                type="email"
                className="bg-[#1a1a24] border-white/10 text-white"
                data-testid="test-email-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)} className="border-white/10 text-gray-300 hover:text-white" data-testid="test-email-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail || !testEmailAddress}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="test-email-send-btn"
              >
                {sendingTestEmail ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><SendHorizontal className="w-4 h-4 mr-2" />Send Test</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
